import { parseAsStringLiteral, useQueryStates } from "nuqs"

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { FilterChip } from "@/components/ui/filter-chip"
import { formatMoney } from "@/lib/formatters"
import { toneDotClass, type StatusTone } from "@/lib/status-colors"
import { cn } from "@/lib/utils"
import { TillFigure } from "@/features/staff/till-figure"
import { ReportFigures, ReportSection } from "@/features/staff/reports/report-block"
import { ForecastChart } from "@/features/staff/reports/charts/forecast-chart"
import { buildForecastChartSeries, sumForecastCents } from "@/features/staff/reports/charts/forecast-series"
import { centsToDecimal } from "@/features/staff/reports/charts/series"
import { formatDayLabel, formatShare, tSharedReports } from "@/features/staff/reports/format"
import {
  DEFAULT_FORECAST_HORIZON,
  FORECAST_HORIZON_KEYS,
  type DayTotal,
  type ForecastHorizonKey,
  type ForecastProps,
  type ForecastResult,
} from "@/features/staff/reports/types"

/**
 * What the model thinks comes next, beside what already happened.
 *
 * **An unavailable card can vanish entirely.** `Staff::Forecast::ReportBlock#history_supports?`
 * gates every horizon option on ONE number. When it also reports `unavailable`,
 * the honest screen has nothing to show. A pending or failed result is still
 * meaningful, though, so it remains visible while its unusable controls hide.
 *
 * **The provenance sentence sits in `CardDescription`, above every number** —
 * the same slot `mix-block.tsx` uses for "no cuadra con las ventas". Every
 * state resolves to exactly one such sentence, so `CardDescription` carries the
 * WHOLE explanation of what is (or is not) below it, and the body never repeats
 * it: a `stale` sentence in the header and a second copy of the same sentence
 * under it would say the same thing twice in two weights, which is the exact
 * failure `sales-block.tsx`'s own header warns against for a different pair of
 * totals.
 *
 * **The hero figure is `text-sm`.** `text-lg` appears exactly once on this
 * screen, on `sales.subtotal` — the only figure that reconciles with a signed Z
 * report. An estimate at the same weight is how a forecast starts being read as
 * a fact instead of a guess, so `TillFigure` is used WITHOUT `emphasis` here,
 * deliberately, even though this is the card's own headline.
 *
 * **`result` and `pace` are siblings, not a ladder.** They answer different
 * questions — "what does the model say" and "at this rate, where does the
 * CURRENT period land" — so both render when they have something to say, and
 * neither is a fallback for the other. A `stale`/`failed`/`pending`/`unavailable`
 * `result` still leaves room for `pace`, which needs nothing from the model at
 * all.
 */

function methodLabel(method: string) {
  return tSharedReports(`forecast.method.${method}`)
}

/**
 * One sentence per state, and the small mark beside it when the state is
 * something the reader should notice rather than a neutral fact. `ready` and
 * `unavailable` carry no dot: one is simply the normal case, the other is an
 * absence rather than a problem.
 */
function forecastStatusTone(result: ForecastResult): StatusTone | null {
  switch (result.state) {
    case "pending":
      return "reserved"
    case "failed":
    case "stale":
      return "warning"
    default:
      return null
  }
}

function forecastStatusSentence(result: ForecastResult): string {
  switch (result.state) {
    case "ready":
      return tSharedReports("forecast.provenance", {
        method: methodLabel(result.forecastMethod),
        as_of: formatDayLabel(result.asOf),
        interval: result.intervalWidth === null ? "—" : formatShare(result.intervalWidth),
      })
    case "stale":
      return tSharedReports("forecast.states.stale", {
        as_of: formatDayLabel(result.asOf),
        count: result.staleByDays,
      })
    case "failed":
      return tSharedReports(
        result.exhausted ? "forecast.states.failed_exhausted" : "forecast.states.failed",
        { count: result.attempts }
      )
    case "pending":
      return tSharedReports("forecast.states.pending")
    case "unavailable":
      return tSharedReports("forecast.states.unavailable")
  }
}

/**
 * The one section that draws a chart: `ready` only. Every other state says why
 * there is none in `CardDescription` instead.
 *
 * **The history half is drawn only when the period runs up to today.** The
 * forecast always starts the day after `asOf`; the period's `byDay` is whatever
 * window the manager selected. Put a January range and an August forecast on one
 * categorical axis and recharts will space them evenly, the seam will join 31
 * January to 27 August with a dashed segment, and the "Hoy" marker will land on
 * a day five months gone. `periodReachesToday` is the server's answer to
 * whether those two halves are actually contiguous
 * (`Staff::ReportPeriod#in_progress?`); when it is false the chart draws the
 * forecast alone rather than inventing a continuity that is not there. The
 * figures around it — the hero, the band, the pace — are unaffected: none of
 * them ever depended on the period.
 */
function ForecastReady({
  result,
  byDay,
}: {
  result: Extract<ForecastResult, { state: "ready" }>
  byDay: DayTotal[]
}) {
  if (result.points.length === 0) return null

  const series = buildForecastChartSeries(byDay, result.points, result.unit)
  const actualSeries = series.filter((point) => point.actualAmount !== null)
  // Matched against `XAxis dataKey="tick"`, so it has to BE a tick. A `key`
  // here is an ISO date the axis never holds, and recharts drops a
  // `ReferenceLine` whose `x` matches no category silently.
  const seamKey = actualSeries.length > 0 ? actualSeries[actualSeries.length - 1].tick : null

  const totals = sumForecastCents(result.points)

  return (
    <>
      {/* `TillFigure` WITHOUT `emphasis`, deliberately: its default IS
          `text-sm`, which is what keeps this figure below `sales.subtotal`'s
          `text-lg` even though this is this card's own headline. */}
      <TillFigure
        testId="report-forecast-hero"
        label={tSharedReports("forecast.hero_label")}
        value={formatMoney(centsToDecimal(totals.mean))}
        hint={tSharedReports("forecast.hero_hint", {
          lower: formatMoney(centsToDecimal(totals.lower)),
          upper: formatMoney(centsToDecimal(totals.upper)),
        })}
      />

      <ForecastChart
        testId="report-forecast-chart"
        points={series}
        seamKey={seamKey}
        note={tSharedReports("forecast.chart_note", {
          history_days: result.historyDays,
          shifts: result.historyShiftCount,
        })}
      />
    </>
  )
}

function ForecastPace({ pace }: { pace: NonNullable<ForecastProps["pace"]> }) {
  return (
    <ReportSection title={tSharedReports("forecast.pace.title")} testId="report-forecast-pace">
      <ReportFigures>
        <TillFigure
          label={tSharedReports("forecast.pace.subtotal_label")}
          value={formatMoney(pace.subtotal)}
          testId="report-forecast-pace-subtotal"
        />
        <TillFigure
          label={tSharedReports("forecast.pace.projected_label")}
          value={formatMoney(pace.projectedSubtotal)}
          testId="report-forecast-pace-projected"
        />
      </ReportFigures>
      <p className="text-xs text-muted-foreground" data-testid="report-forecast-pace-hint">
        {tSharedReports("forecast.pace.hint", {
          service_days: pace.serviceDays,
          projected_service_days: pace.projectedServiceDays,
          remaining_days: pace.remainingCalendarDays,
        })}
      </p>
    </ReportSection>
  )
}

export function ReportForecastBlock({
  forecast,
  byDay,
}: {
  forecast: ForecastProps
  byDay: DayTotal[]
}) {
  // Only the setter is used for rendering decisions — `forecast.horizon` is the
  // server's own echo of what actually rendered, matching how `period.preset`
  // (not the URL) decides which chip is active in `ReportPeriodBar`.
  const [, setQuery] = useQueryStates(
    { horizon: parseAsStringLiteral(FORECAST_HORIZON_KEYS).withDefault(DEFAULT_FORECAST_HORIZON) },
    // Deliberately NOT shallow, and `clearOnDefault: false` for the same reason
    // `period` carries it: this URL gets bookmarked and shared, and
    // `NuqsInertiaAdapter` ignores nuqs' own `shallow` option because
    // `usePage().url` never updates on a raw `history` write — only a real
    // Inertia visit resolves a new horizon server-side.
    { clearOnDefault: false }
  )

  const hasEnabledHorizons = forecast.horizons.some((option) => option.enabled)
  const result = forecast.result

  // An unavailable result with no supported horizon has nothing to disclose.
  // Pending and failed results still communicate model status and can retain
  // the independently-derived pace projection, without a disabled control row.
  if (!hasEnabledHorizons && result.state === "unavailable") return null

  function selectHorizon(value: ForecastHorizonKey) {
    setQuery({ horizon: value })
  }

  const tone = forecastStatusTone(result)

  return (
    <Card density="compact" data-testid="report-forecast">
      <CardHeader className="border-b">
        <CardTitle>{tSharedReports("forecast.title")}</CardTitle>
        <CardDescription className="flex items-center gap-2">
          {tone ? (
            <span aria-hidden="true" className={cn("shrink-0", toneDotClass(tone, "size-2"))} />
          ) : null}
          <span data-testid="report-forecast-provenance">{forecastStatusSentence(result)}</span>
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {hasEnabledHorizons ? (
          <div className="flex flex-wrap gap-2" data-testid="report-forecast-horizons">
            {forecast.horizons.map((option) => (
              <FilterChip
                key={option.value}
                active={forecast.horizon === option.value}
                disabled={!option.enabled}
                className="h-11 md:h-9"
                data-testid={option.testId}
                onClick={() => selectHorizon(option.value)}
              >
                {option.label}
              </FilterChip>
            ))}
          </div>
        ) : null}

        {result.state === "ready" ? (
          <ForecastReady result={result} byDay={forecast.periodReachesToday ? byDay : []} />
        ) : null}

        {forecast.pace ? <ForecastPace pace={forecast.pace} /> : null}

        <p className="text-xs text-muted-foreground" data-testid="report-forecast-coverage">
          {forecast.coverageNote}
        </p>
      </CardContent>
    </Card>
  )
}
